import { useLayoutEffect, useRef } from 'react';
import styles from './Tooltip.module.css';

const VIEWPORT_MARGIN = 8;

interface TooltipProps {
  label: string;
  shortcut?: string;
  shortcuts?: readonly string[];
  visible?: boolean;
}

export function Tooltip({ label, shortcut, shortcuts, visible }: TooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shortcutItems = shortcuts?.length ? shortcuts : shortcut ? [shortcut] : [];

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const getContainer = (): { left: number; right: number } => {
      const playerRoot = el.closest<HTMLElement>('[data-player-root]');
      if (playerRoot) return playerRoot.getBoundingClientRect();
      return { left: 0, right: window.innerWidth };
    };

    const adjust = () => {
      el.style.transform = 'translateX(-50%)';
      const rect = el.getBoundingClientRect();
      const container = getContainer();
      if (rect.right > container.right - VIEWPORT_MARGIN) {
        const overflow = rect.right - container.right + VIEWPORT_MARGIN;
        el.style.transform = `translateX(calc(-50% - ${overflow}px))`;
      } else if (rect.left < container.left + VIEWPORT_MARGIN) {
        const overflow = container.left + VIEWPORT_MARGIN - rect.left;
        el.style.transform = `translateX(calc(-50% + ${overflow}px))`;
      }
    };

    adjust();

    const playerRoot = el.closest<HTMLElement>('[data-player-root]');
    const target = playerRoot ?? document.documentElement;
    const ro = new ResizeObserver(adjust);
    ro.observe(target);
    ro.observe(el); // re-adjust when tooltip gains a layout box (controls appear from display:none)
    return () => ro.disconnect();
  }, [label, shortcut, shortcuts]);

  return (
    <div ref={ref} className={`${styles.tooltip}${visible ? ` ${styles.visible}` : ''}`} role="tooltip">
      <span className={styles.label}>{label}</span>
      {shortcutItems.map((item, index) => (
        <span key={`${item}-${index}`} className={styles.shortcut}>{item}</span>
      ))}
    </div>
  );
}
